'use client';

/**
 * ObeyaRoomStoryCard — POC scrollytelling read of the room comparison:
 * a hero stat row, then one "beat" per room type with a real per-room
 * price range and an LLM-written narrative.
 *
 * Explicitly a concept/inspiration page, not a production feature — see
 * room_story.py's module docstring for what's deliberately not built
 * (caching on the model call). Not a replacement for
 * ObeyaRoomComparisonCard, which stays the exact-numbers tool; this is
 * a narrated read of the SAME facts that card already computes. No
 * editing, no client-side computation — every number and every word of
 * narrative comes from the BFF (room_story strategy), including the
 * narrative text itself (no hardcoded copy in this component).
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaRoomStoryCard
 * @see klyde_edge/klyde_intelligence/projects/room_story.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface TierTotals {
  budget: number | null; mid: number | null; premium: number | null;
  resolved_requirements: number; unresolved_requirements: number;
}
interface RoomStoryRow {
  key: string; label: string;
  vendor_count: number;
  requirements_total: number; requirements_selected: number; candidates_total: number;
  tier_totals: TierTotals;
  // Vendor-coverage figure, NOT a room total — a vendor's own quoted
  // items summed, possibly a partial subset of the room's real needs.
  // Kept as a small secondary note; tier_totals is the real headline.
  price_range: { min: number; max: number } | null;
  insight: string;
}
interface RoomStoryHero { room_type_count: number; total_items: number; total_vendors: number }
interface RoomStoryData { rooms: RoomStoryRow[]; hero: RoomStoryHero }

export function ObeyaRoomStoryCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'room_story'));
  const { data, isLoading } = useStrategyData<RoomStoryData>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy,
  );

  const rooms = data?.data?.rooms ?? [];
  const hero = data?.data?.hero;

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_room_story',
    status: (data ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: strategy ? ['strategy-data', strategy, { semantic_id: mcpId ?? '' }] : null,
    text: `Room story: ${rooms.length} room types`,
    json: data ? { rooms, hero } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, data, mcpId, strategy, rooms, hero]));

  if (!mcpId) return null;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className={cn(text.eyebrow, 'text-primary')}>POC · concept page</div>
        <p className={cn(text.muted, 'mt-2 max-w-[52ch]')}>
          A narrated read of the same room comparison — real per-room price ranges, real vendor and item
          counts, and an LLM-written summary constrained to those facts. Not a replacement for the exact
          numbers in Room Comparison.
        </p>

        {isLoading && rooms.length === 0 ? (
          <div className={cn(text.muted, 'mt-10 text-center')}>Generating…</div>
        ) : rooms.length === 0 ? (
          <div className={cn(text.muted, 'mt-10 text-center')}>No rooms with placements yet</div>
        ) : (
          <>
            {hero && (
              <div className="grid grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden mt-8">
                <div className="bg-card px-5 py-6">
                  <div className="text-4xl font-serif tabular-nums">{hero.room_type_count}</div>
                  <div className={cn(text.muted, 'mt-2')}>room types with placements</div>
                </div>
                <div className="bg-card px-5 py-6">
                  <div className="text-4xl font-serif tabular-nums">{hero.total_items}</div>
                  <div className={cn(text.muted, 'mt-2')}>candidate items placed</div>
                </div>
                <div className="bg-card px-5 py-6">
                  <div className="text-4xl font-serif tabular-nums">{hero.total_vendors}</div>
                  <div className={cn(text.muted, 'mt-2')}>vendors in the running</div>
                </div>
              </div>
            )}

            <div className="mt-4">
              {rooms.map((room) => (
                <div key={room.key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-8 items-start py-12 border-t border-border">
                  <div>
                    {room.tier_totals.budget != null ? (
                      <>
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <span className="text-3xl font-serif tabular-nums">${Math.round(room.tier_totals.budget).toLocaleString()}</span>
                          <span className="text-3xl font-serif tabular-nums text-primary">${Math.round(room.tier_totals.mid!).toLocaleString()}</span>
                          <span className="text-3xl font-serif tabular-nums">${Math.round(room.tier_totals.premium!).toLocaleString()}</span>
                        </div>
                        <div className={cn(text.muted, 'mt-1')}>
                          budget · mid · premium — furnishing every requirement at that tier
                          {room.tier_totals.unresolved_requirements > 0 && (
                            <> ({room.tier_totals.resolved_requirements} of {room.tier_totals.resolved_requirements + room.tier_totals.unresolved_requirements} requirements)</>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className={cn(text.muted)}>Not enough resolved requirements to estimate tier totals</div>
                    )}
                    <div className={cn(text.heading, 'mt-4')}>{room.label}</div>
                    <div className={cn(text.muted, 'mt-1')}>
                      {room.requirements_selected} of {room.requirements_total} requirements decided ·{' '}
                      {room.candidates_total} candidate{room.candidates_total !== 1 ? 's' : ''} · {room.vendor_count} vendor{room.vendor_count !== 1 ? 's' : ''}
                    </div>
                    {room.price_range && (
                      <div className={cn(text.muted, 'mt-1 text-muted-foreground/70')}>
                        Vendor quotes for what's covered span ${room.price_range.min.toLocaleString()}
                        {room.price_range.max !== room.price_range.min && ` – $${room.price_range.max.toLocaleString()}`}
                        {' '}(not a full-room total — coverage varies by vendor)
                      </div>
                    )}
                  </div>
                  <div>
                    {room.insight ? (
                      <p className={cn(text.body, 'leading-relaxed')}>{room.insight}</p>
                    ) : (
                      <p className={cn(text.muted)}>Nothing stands out for this room.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
